/**
 * Map Growth Engine recommendation CTA actions → dashboard paths.
 * Standing orders are app-only until dashboard can create them — no fake
 * route to Purchasing / Orders.
 *
 * open_orders → Shopping lists; Catalogue when the shopping list is empty.
 * Menu Builder / par / stock take can carry the suggested product on the route
 * so the feature opens with that line already in play.
 */

import { ollieOrderingShoppingListsPath } from './ollieOrderingHandoff'
import {
  appendGrowthEngineSuggestedProduct,
  growthEngineActionPutsProductInPlay,
} from './growthEngineSuggestedProduct'

const CTA_ROUTE_BUILDERS = Object.freeze({
  add_supplier: venueId => `/v/${venueId}/suppliers`,
  restrict_supplier: venueId => `/v/${venueId}/suppliers`,
  open_par_levels: venueId => `/v/${venueId}/products`,
  start_stock_take: venueId => `/v/${venueId}/stockTakes`,
  open_menu_builder: venueId => `/v/${venueId}/menu-builder`,
  upload_invoice: venueId => `/v/${venueId}/purchasing/invoices`,
  open_shopping_lists: venueId => `/v/${venueId}/products`,
  open_catalogue: venueId => `/v/${venueId}/catalogue`,
  open_basket: venueId => `/v/${venueId}/purchasing/basket`,
  open_goods_receipting: venueId => `/v/${venueId}/purchasing/receipting`,
  /**
   * Receipting empty with 0 POs — go to Orders and open manual add.
   * Distinct from open_orders (shopping lists / catalogue).
   */
  add_or_place_order: venueId =>
    `/v/${venueId}/purchasing/orders?addManualOrder=1`,
  /**
   * Start / resume ordering — shopping lists by default; catalogue when the
   * list has nothing to order from yet.
   */
  open_orders: (venueId, options = {}) =>
    options.shoppingListEmpty
      ? `/v/${venueId}/catalogue`
      : `/v/${venueId}/products`,
  /** Confirm-an-Ollie-basket job — Shopping Lists with the order composer open. */
  open_ollie_ordering: venueId => ollieOrderingShoppingListsPath(venueId),
})

/**
 * @param {string} venueId
 * @param {string|null|undefined} action
 * @param {{
 *   shoppingListEmpty?: boolean,
 *   product?: string,
 *   productTitle?: string,
 *   productId?: string,
 * }} [options]
 * @returns {string|null}
 */
/** Express still sends these; dashboard cannot complete them. */
export const DASHBOARD_APP_ONLY_CTA_ACTIONS = Object.freeze([
  'create_standing_order',
  'open_standing_orders',
])

export const STANDING_ORDERS_DASHBOARD_HONEST_NOTE =
  'Do this in the OMS app — the dashboard cannot create a standing order yet.'

export function isDashboardAppOnlyCtaAction(action) {
  return DASHBOARD_APP_ONLY_CTA_ACTIONS.includes(action)
}

export function resolveGrowthEngineCtaPath(venueId, action, options = {}) {
  if (!venueId || !action) return null
  const builder = CTA_ROUTE_BUILDERS[action]
  if (!builder) return null
  const path = builder(venueId, options)
  if (!path) return null
  if (
    growthEngineActionPutsProductInPlay(action) ||
    options.fromProgress ||
    options.openCreate ||
    options.recipeId
  ) {
    return appendGrowthEngineSuggestedProduct(path, options)
  }
  return path
}

export function listGrowthEngineCtaActions() {
  return [...Object.keys(CTA_ROUTE_BUILDERS), ...DASHBOARD_APP_ONLY_CTA_ACTIONS]
}
